/**
 * supabaseStammdaten.ts — DB-Schicht für die Mandanten-Self-Erfassung der
 * Stammdaten (Modul 00).
 *
 * Verwendet das normale `cases`-UPDATE — die DB-seitige Schutz-Logik in
 * Migration 0018 (cases_protect_mandant_fields-Trigger) sorgt dafür, dass
 * der Mandant nur die Whitelist-Felder ändern darf. Wenn ein Berater
 * dieselbe Funktion benutzt, gehen alle Felder durch.
 *
 * Bei Trigger-Verletzung wirft Postgres mit ERRCODE = 'insufficient_privilege'
 * — die Fehlermeldung ist menschenlesbar (z.B. „Mandant darf status nicht
 * ändern"), die wir 1:1 nach oben durchreichen.
 */

import { getSupabaseClient } from '../supabase';
import type { Database } from '../supabaseTypes';
import type { StammdatenDraft } from './stammdatenValidation';
import { draftToCaseUpdate } from './stammdatenValidation';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Schreibt die Mandanten-Stammdaten in die `cases`-Tabelle. RLS+Trigger
 * regeln die Felder-Whitelist serverseitig.
 *
 * Liefert das aktualisierte CaseRow zurück, damit Konsumenten optimistisch
 * den lokalen Stand aktualisieren können.
 */
export async function updateCaseStammdaten(caseId: string, draft: StammdatenDraft): Promise<void> {
  if (!UUID_RE.test(caseId)) {
    throw new Error('updateCaseStammdaten: caseId ist kein UUID-Format');
  }
  const sb = getSupabaseClient();
  const update = draftToCaseUpdate(draft) as Database['public']['Tables']['cases']['Update'];

  const { error } = await sb
    .from('cases')
    .update(update as never)
    .eq('id', caseId)
    .is('deleted_at', null);

  if (error) {
    // Trigger-Fehler werden mit code = 42501 (insufficient_privilege) und
    // message wie „cases: Mandant darf X nicht ändern" geliefert. Wir
    // reichen sie 1:1 weiter — die UI zeigt das in der Save-Status-Zeile.
    throw new Error(`updateCaseStammdaten [${error.code ?? '??'}]: ${error.message}`);
  }
}
